// Copyright (c) 2019-2023 Cigent Technology Inc. All Rights Reserved.
import { Component, OnInit } from '@angular/core';
import { HeaderService } from 'src/app/header.service';
import { D3eService } from 'src/services/d3e-service';
import { IIntegration, PartnerTypes, IIntegrationsPageConfig, AzureIntegratonSource } from 'src/types/integrations';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { NotificationService } from 'src/services/notification-service';
import { AlertDialogComponent } from 'src/app/dialogs/alert/alert.component';
import {from, Observable} from 'rxjs';
import { SelectedOrgMethod } from 'src/shared/selectedOrg';
import { TranslateService } from '@ngx-translate/core';
import {RestConfigService} from '../../../services/config-service';
import { Theme, ThemeService } from '../../../services/theme.service';
import {mergeMap} from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ConfigurationService } from '../../../services/configuration-service';
import { ClipboardService } from '../../../services/clipboard.service';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-integrations',
    templateUrl: './integrations.component.html',
    styleUrls: ['./integrations.component.scss'],
    standalone: false
})
export class IntegrationsComponent implements OnInit {
  THIRD_PARTY = PartnerTypes.THIRD_PARTY;
  CARBON_BLACK = PartnerTypes.CARBON_BLACK;
  CYBERARK_EPM = PartnerTypes.CYBERARK_EPM;
  SOPHOS = PartnerTypes.SOPHOS;
  SENTINEL_ONE = PartnerTypes.SENTINEL_ONE;
  CISCO_AMP = PartnerTypes.CISCO_AMP;
  CISCO_DUO = PartnerTypes.CISCO_DUO;
  ACTIVE_DIRECTORY = PartnerTypes.ACTIVE_DIRECTORY;
  OKTA = PartnerTypes.OKTA;
  CIGENT_TESTING = PartnerTypes.CIGENT_TESTING;

  active_directory_enabled = false;
  showAzureDirectory = false;
  showOkta = false;

  theme$: Observable<Theme>;
  sophos: IIntegration = null;
  carbonBlack: IIntegration = null;
  cyberark: IIntegration = null;
  sentinelOne: IIntegration = null;
  ciscoAmp: IIntegration = null;
  ciscoDuo: IIntegration = null;
  activeDirectory: IIntegration = null;
  okta: IIntegration = null;
  deleteEnabled = true;
  sophosLocal = false;
  carbonBlackLocal = false;
  cigentTesting: IIntegration = null;

  apiTokens: IIntegration[] = [];

  selectedOrgId: string;
  hasUserBeenSet = false;


  readonly columns: string[] = ['display_name', 'sensor_type', 'description', 'buttons'];
  config: IIntegrationsPageConfig = {
    carbon_black_button: false,
    sophos_button: false,
    cisco_amp_button: false,
    cisco_duo_button: false,
    cisco_duo_button_disabled: true,
  };

  fCloud: string;
  fConfig: string;
  fSophos: string;
  fSophosLocal: string;
  fCBC: string;
  fCBCLocal: string;
  fCisco: string;
  fCiscoCloud: string;
  fStream: string;
  fStreamUID: string;
  fStreamID: string;
  fColon: string;
  fEPM: string;
  fEPMC: string;
  fD3EThird: string;
  fD3EThirdDesc: string;
  fAPIToken: string;
  fGenToken: string;
  fName: string;
  fThreatNumber: string;
  fThreatDesc: string;
  fAction: string;
  fCopy: string;
  fDelete: string;
  fNoTokens: string;
  fUpdateSuccessful: string;
  fDeleteConfirmTitle: string;
  fDeleteConfirmText: string;
  fLocalEnableFailed: string;
  fFailedToDelete: string;
  fFailedToEnable: string;
  fYes: string;
  fNo: string;
  showTestIntegration: boolean;

  title: string;
  integrationsLoaded = false;

  constructor(
    private d3eService: D3eService, 
    private headerService: HeaderService,
    private router: Router,
    public dialog: MatDialog,
    private notification: NotificationService,
    public translate: TranslateService,
    private restConfig: RestConfigService,
    private themeService: ThemeService,
    private configure: ConfigurationService,
    private readonly clipboard: ClipboardService,
  ) {
    this.translate.get(['INT_CLOUD', 'INT_CONFIG', 'INT_SOPHOS', 'INT_INTEND_LOCAL', 'INT_CB',
      'INT_CB_LOCAL', 'INT_CIS', 'INT_CISC', 'INT_STREAM', 'INT_STREAM_UID', 'INT_STREAM_ID',
      'INT_COLON', 'INT_EPM', 'INT_EPMC', 'INT_D3E_HEADER', 'INT_D3E_CONFIG', 'INT_API_TOKEN',
      'INT_GEN_TOKEN', 'NAME', 'INT_THREAT_NUMB', 'INT_THREAT_DESC', 'INT_ACTION', 'INT_COPY', 'INT_DELETE',
      'INT_NO_TOKENS', 'TITLE_INT', 'UPDATE_COMPLETE', 'INT_DELETE_CONFIRM_TEXT', 'INT_DELETE_CONFIRM_TITLE',
      'INT_FAILED_TO_DELETE', 'INT_FAILED_TO_ENABLE', 'INT_LOCAL_ENABLE_FAILED', 'YES', 'NO',
      'SELECTOR_YOUR_INTEGRATIONS']).subscribe(vals => {
        this.fCloud = vals['INT_CLOUD'];
        this.fConfig = vals['INT_CONFIG'];
        this.fSophos = vals['INT_SOPHOS'];
        this.fSophosLocal = vals['INT_INTEND_LOCAL'];
        this.fCBC = vals['INT_CB'];
        this.fCBCLocal = vals['INT_CB_LOCAL'];
        this.fCisco = vals['INT_CIS'];
        this.fCiscoCloud = vals['INT_CISC'];
        this.fStream = vals['INT_STREAM'];
        this.fStreamUID = vals['INT_STREAM_UID'];
        this.fStreamID = vals['INT_STREAM_ID'];
        this.fColon = vals['INT_COLON'];
        this.fEPM = vals['INT_EPM'];
        this.fEPMC = vals['INT_EPMC'];
        this.fD3EThird = vals['INT_D3E_HEADER'];
        this.fD3EThirdDesc = vals['INT_D3E_CONFIG'];
        this.fAPIToken = vals['INT_API_TOKEN'];
        this.fGenToken = vals['INT_GEN_TOKEN'];
        this.fName = vals['NAME'];
        this.fThreatNumber = vals['INT_THREAT_NUMB'];
        this.fThreatDesc = vals['INT_THREAT_DESC'];
        this.fAction = vals['INT_ACTION'];
        this.fCopy = vals['INT_COPY'];
        this.fDelete = vals['INT_DELETE'];
        this.fNoTokens = vals['INT_NO_TOKENS'];
        this.fUpdateSuccessful = vals['UPDATE_COMPLETE'];
        this.fDeleteConfirmTitle = vals['INT_DELETE_CONFIRM_TITLE'];
        this.fDeleteConfirmText = vals['INT_DELETE_CONFIRM_TEXT'];
        this.fLocalEnableFailed = vals['INT_LOCAL_ENABLE_FAILED'];
        this.fFailedToDelete = vals['INT_FAILED_TO_DELETE'];
        this.fFailedToEnable = vals['INT_FAILED_TO_ENABLE'];
        this.fYes = vals['YES'];
        this.fNo = vals['NO'];

        this.title = vals['TITLE_INT'];

        this.headerService.setOrgSelectorDefaultOption(vals['SELECTOR_YOUR_INTEGRATIONS']);
        // @ts-ignore
        let showIntegrationValue = ( configure.showTestIntegration == 'true' || configure.showTestIntegration == true );
        this.showTestIntegration = showIntegrationValue;
    });

    this.theme$ = this.themeService.getTheme();
    this.headerService.setTitle(this.title);
    this.headerService.setSubtitle('');
    this.headerService.setIcon('mdi-sync');
    this.headerService.setNavMenuText('');
    this.headerService.showUserFilter(true);

    this.showAzureDirectory = this.configure.showAzureLogin;
    this.showOkta = this.configure.showOktaLogin;

    this.headerService.orgSelectedChanged
        .pipe(untilDestroyed(this))
        .subscribe(value => {
          this.selectedOrgId = value;
          if ( this.hasUserBeenSet ) {
            this.getIntegrations();
          }
          this.hasUserBeenSet = true;
    });
    this.restConfig.getRestConfig()
      .pipe(untilDestroyed(this))
      .subscribe(settings => {
        if (settings) {
          this.active_directory_enabled = settings.azure_active_directory_allowed;
        }
    });
  }

  ngOnInit() {
    this.getIntegrationsConfiguration();
    this.getIntegrations();
  }

  getIntegrationsConfiguration() {
    this.d3eService.getIntegrationsConfig()
      .pipe(untilDestroyed(this))
      .subscribe(config => this.config = config);
  }

  getIntegrations() {
    this.integrationsLoaded = false;
    const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);

    this.d3eService.get_event_log_monitors(selectedOrg)
      .pipe(untilDestroyed(this))
      .subscribe(response => {
        let carbonBlackLocal = false;
        let sophosLocal = false;
        response.sensor_ids.forEach(sensor_id => {
          switch (sensor_id) {
            case 1199:
              carbonBlackLocal = true;
              break;

            case 1198:
              sophosLocal = true;
              break;
          }
        });
        this.carbonBlackLocal = carbonBlackLocal;
        this.sophosLocal = sophosLocal;
    });
    this.d3eService.get_integrations(selectedOrg)
      .pipe(untilDestroyed(this))
      .subscribe(integrations => {
        this.sophos = null;
        this.sentinelOne = null;
        this.carbonBlack = null;
        this.cyberark = null;
        this.ciscoAmp = null;
        this.ciscoDuo = null;
        this.activeDirectory = null;
        this.okta = null;
        this.cigentTesting = null;
        this.apiTokens = [];
        integrations.forEach(((integration: IIntegration) => {
          switch (integration.partner_id) {

            case this.SOPHOS:
              this.sophos = integration;
              break;

            case this.SENTINEL_ONE:
              this.sentinelOne = integration;
              break;

            case this.CARBON_BLACK:
              this.carbonBlack = integration;
              break;

            case this.CISCO_AMP:
              this.ciscoAmp = integration;
              break;

            case this.ACTIVE_DIRECTORY:
              this.activeDirectory = integration;
              this.deleteEnabled = this.activeDirectory.config.source !== AzureIntegratonSource.AZURE_SERVICE;
              break;

            case this.OKTA:
              this.okta = integration;
              break;

            case this.CISCO_DUO:
              this.ciscoDuo = integration;
              break;

            case this.CYBERARK_EPM:
              this.cyberark = integration;
              break;

            case this.THIRD_PARTY:
              this.apiTokens.push(integration);
              break;

            case this.CIGENT_TESTING:
              this.cigentTesting = integration;

            }
        }).bind(this));
        this.integrationsLoaded = true;
      });
  }

  createThirdPartyIntegration() {
    this.router.navigateByUrl('/third-party/new');
  }

  createCarbonBlackIntegration() {
    this.router.navigateByUrl('/carbon-black/new');
  }

  createCyberArkEpmIntegration() {
    this.router.navigateByUrl('/cyberark-epm/new');
  }

  createSophosIntegration() {
    this.router.navigateByUrl('/sophos/new');
  }

  createCiscoAmpIntegration() {
    this.router.navigateByUrl('/cisco-amp/new');
  }

  createCiscoDuoIntegration() {
    this.router.navigateByUrl('/cisco-duo/new');
  }

  partnerForId(id: number) {
    if (id === this.THIRD_PARTY) {
      return 'Third Party';
    } else if (id === this.CARBON_BLACK) {
      return 'Carbon Black';
    } else if (id === this.CYBERARK_EPM) {
      return 'CyberArk EPM';
    } else if (id === this.SOPHOS) {
      return 'Sophos';
    } else if (id === this.CISCO_AMP) {
      return 'Cisco Secure Endpoint';
    } else if (id === this.CISCO_DUO) {
      return 'Cisco Duo';
    }
  }

  partnerRouteId(id: number) {
    if (id === this.THIRD_PARTY) {
      return 'third-party';
    } else if (id === this.CARBON_BLACK) {
      return 'carbon-black';
    } else if (id === this.CYBERARK_EPM) {
      return 'cyberark-epm';
    } else if (id === this.SOPHOS) {
      return 'sophos';
    } else if (id === this.CISCO_AMP) {
      return 'cisco-amp';
    } else if (id === this.CISCO_DUO) {
      return 'cisco-duo';
    }
  }

  deleteIntegration(integrationId: string) {
    const dialogRef = this.dialog.open(AlertDialogComponent, {
      width: '450px',
      data: {
        title: this.fDeleteConfirmTitle,
        content: this.fDeleteConfirmText,
        rejectBtnName: this.fNo,
        acceptBtnName: this.fYes,
        showRejectBtn: true,
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);

        this.d3eService.delete_integration(integrationId, selectedOrg)
          .subscribe(() => {
            this.getIntegrations();
          }, this.reportDeleteError.bind(this));
      }
    });
  }

  reportDeleteError() {
    this.notification.showError(this.fFailedToDelete);
  }

  reportEnableError() {
    this.notification.showError(this.fFailedToEnable);
    this.getIntegrations();
  }

  toggleIntegration(integration: {id: string, enabled: boolean}) {
    const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);

    this.d3eService.set_integration_enabled(integration.id, integration.enabled, selectedOrg)
      .subscribe(() => {
        this.getIntegrations();
    }, this.reportEnableError.bind(this));
  }

  copy(token: string) {
    this.clipboard.copyMessage(token, this.translate.instant('COPIED_TO_CLIPBOARD')).then(null);
  }

  onEnabledChanged(id: string, toggle: MatSlideToggleChange) {
    const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);

    this.d3eService.set_integration_enabled(id, toggle.checked, selectedOrg)
      .subscribe(() => {
        this.notification.showSuccess(this.fUpdateSuccessful);
        this.getIntegrations();
    }, this.reportEnableError.bind(this));
  }

  onLocalEnabledChanged(type: string, toggle: MatSlideToggleChange) {
    let sensorTypes: number[] = [];
    if (type === 'carbon-black') {
      sensorTypes = [1199];
    } else if (type === 'sophos') {
      sensorTypes = [1198, 1197];
    }
    const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);

    if (sensorTypes.length === 0) {
      return;
    }

    from(sensorTypes).pipe(
      mergeMap(sensorType => this.d3eService.set_event_log_monitor_enabled(sensorType, toggle.checked, selectedOrg), 2)
    ).subscribe( () => {},
        error => {
          this.notification.showError(this.fLocalEnableFailed);
          this.getIntegrations();
    });
  }
}
